import {
  GRID_CHECKBOX_SELECTION_FIELD,
  GridCallbackDetails,
  GridColDef,
  GridInitialState,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { EditorialMiniMenuConfig } from 'components/Minimenus/EditorialMiniMenu';
import { startTimeDesc } from 'components/Search/const';
import { isDeepEqual } from 'remeda';
import { hasLength, hasValue } from 'utility/functions';
import {
  FiltersVisibility,
  SearchModalFilters,
  SearchModalProps,
  SupportedResources,
} from './SearchModal';
import { GenericSearchItem } from 'components/Search/components/SearchItemsProvider';

/**
 * --------------------------------------------------------
 * Types / Functions / Constants for Search Modal
 * --------------------------------------------------------
 */

/**
 *
 * Get local storage key for view type
 */
export function getSearchModalViewLSKey(resource: SupportedResources): string {
  const LS_KEY_BASE = 'search-modal-view';
  const storageKey = `${LS_KEY_BASE}-${resource}`;
  return storageKey;
}

/**
 * Which advanced filters are visible/applied
 * Returns boolean flags for each advanced filter
 */
export function getAdvFiltersVisiblity(filters: SearchModalFilters): FiltersVisibility {
  const hasDateRangeMinMax =
    hasLength(filters?.['start_time[min]']) || hasLength(filters?.['start_time[max]']);

  const hasPrintDepthRangeMinMax =
    hasLength(filters?.['print_depth[min]']) || hasLength(filters?.['print_depth[max]']);

  const hasPrintPublicationDateMinMax =
    hasLength(filters?.['print_publication_date[min]']) ||
    hasLength(filters?.['print_publication_date[max]']);

  const showWorkflows =
    hasLength(filters?.workflow) || hasLength(filters?.workflow_process);

  const visibility: FiltersVisibility = {
    showAdvertorial: hasLength(filters?.advertorial),
    showApp: hasLength(filters?.app),
    showAuthor: hasLength(filters?.author),
    showContributed: hasLength(filters?.contributed),
    showDeleted: hasLength(filters?.deleted),
    showDraft: hasLength(filters?.draft),
    showFlag: hasLength(filters?.flag),
    showPhotoSales: hasLength(filters?.photo_sales),
    showPrintDepthRange: hasPrintDepthRangeMinMax,
    showPrintPublication: hasLength(filters?.print_publication),
    showPrintPublicationDateRange: hasPrintPublicationDateMinMax,
    showPrintPublicationStatus: hasLength(filters?.print_publication_status),
    showPrintReady: hasLength(filters?.print_ready),
    showPrintSlug: hasLength(filters?.print_slug),
    showPublishStatus: hasLength(filters?.publish_status),
    showSection: hasLength(filters?.section),
    showStartTime: hasDateRangeMinMax,
    showTemplate: hasLength(filters?.template),
    showType: hasLength(filters?.type),
    showWorkflow: showWorkflows,
  };

  return visibility;
}

/**
 * Updates single key in filters object.
 *
 * Accepts filters object, key, and value.
 * Returns updated filters object.
 * - Removes key if value is empty
 * - (null, undefined, '', or empty array)
 * - Else will set the value as provided
 */

type FormatFilterUpdateArgs = {
  filters: SearchModalFilters;
  key: keyof SearchModalFilters;
  value: string | string[];
};

export function formatFilterUpdate({
  filters,
  key,
  value,
}: FormatFilterUpdateArgs): SearchModalFilters {
  const updated = { ...filters };

  const isFalsy = !hasValue(value);
  const isEmptyArray = Array.isArray(value) && value.length === 0;

  if (isFalsy || isEmptyArray) {
    delete updated[key];
  } else {
    updated[key] = value;
  }

  return updated;
}

/**
 * --------------------------------------------------------
 * Get default sort/dir filters per resource
 * --------------------------------------------------------
 */
export function getDefaultFilters(resource: SupportedResources): SearchModalFilters {
  switch (resource) {
    case 'editorial': {
      return startTimeDesc;
    }
    // Empty / no defaults
    default: {
      const empty = {};
      return empty;
    }
  }
}

/**
 * Get default sort/dir config per resource
 * - sortKey: eg filters['sortKey'] - field
 * - dirKey: eg filters['dirKey'] - direction
 * - uppercaseDir: filter dir uppercase (ASC/DESC or asc/desc)
 */

export type SortDirConfig = {
  sortKey: string;
  dirKey: string;
  uppercaseDir: boolean;
};

export function getSortDirConfig(resource: SupportedResources): SortDirConfig {
  switch (resource) {
    case 'editorial': {
      return {
        sortKey: 'sort',
        dirKey: 'dir',
        uppercaseDir: true,
      };
    }
    default: {
      return {
        sortKey: 'sort',
        dirKey: 'dir',
        uppercaseDir: true,
      };
    }
  }
}

/**
 * --------------------------------------------------------
 * Get default query text key per resource
 * - eg: filters['query'] or filters['search']
 * Most APIs will use 'query' but some differ...
 * --------------------------------------------------------
 */

export function getQueryTextKey(resource: SupportedResources): string {
  switch (resource) {
    case 'editorial': {
      return 'query';
    }
    default: {
      return 'query';
    }
  }
}

/**
 * --------------------------------------------------------
 * Get default minimenu config per resource
 * - Confugre mini menu within the search modals
 * --------------------------------------------------------
 */

export const searchModalEditorialMenuOpts: EditorialMiniMenuConfig = {
  export: { enabled: false },
  delete: { enabled: false },
  duplicate: { enabled: false },
  edit: { enabled: true },
  liveView: { enabled: true },
  preview: { enabled: true },
  clearSelections: { enabled: true },
};

type GetDefaultMiniMenuConfigArgs = {
  resource: SupportedResources;
};

export type GetDefaultMiniMenuConfigResult = EditorialMiniMenuConfig | null;

export function getDefaultMiniMenuConfig({
  resource,
}: GetDefaultMiniMenuConfigArgs): GetDefaultMiniMenuConfigResult {
  switch (resource) {
    case 'editorial': {
      return searchModalEditorialMenuOpts;
    }
    default: {
      return null;
    }
  }
}

/**
 * Helper to check if a card/row select should
 * be disabled for a given record. Accepts a
 * custom checker function and record.
 *
 * Note: this may be different than disabled
 * styling in some cases. An item can be selectable
 * but styled as disabled (eg: deleted items)...
 */

export type CheckSelectDisabledArgs = {
  resource: SupportedResources;
  record?: GenericSearchItem | null;
  checkItemSelectDisabled?: SearchModalProps['checkItemSelectDisabled'];
};

export function checkSelectDisabled({
  resource,
  record,
  checkItemSelectDisabled,
}: CheckSelectDisabledArgs): boolean {
  // Ensure record exists
  if (!record) {
    return true;
  }
  // Use function if provided
  if (typeof checkItemSelectDisabled === 'function') {
    return checkItemSelectDisabled(record);
  }
  // Default checks per resource
  switch (resource) {
    case 'editorial': {
      // Selections must contain a valid uuid
      const hasUuid = Boolean(record?.uuid);
      if (!hasUuid) {
        return true;
      }
      return false;
    }
    default: {
      return false;
    }
  }
}

/**
 * --------------------------------------------------------
 * Utils and Helpers for MUI Datagrids
 * --------------------------------------------------------
 */

/**
 * Get grid state local storage key - view: 'list' | 'summary'
 */
export function getGridStorageLSKey(resource: SupportedResources, view: string): string {
  const LS_KEY_BASE = 'search-modal-datagrid-state';
  return `${LS_KEY_BASE}-${resource}-${view}`;
}

type GenericGridColDefs = GridColDef<any>[];
type GridInitialStatePro = GridInitialState;

type AugmentGridStateArgs = {
  gridState: GridInitialStatePro;
  colDefs: GenericGridColDefs;
  filters: SearchModalFilters;
  baseGridInitialState: GridInitialStatePro;
  sortKey: string; // via sortDirConfig
  dirKey: string;
};

/**
 * Helper to augment grid state based filters
 * and other various workarounds with MUI DG Pro
 */
export function augmentGridState({
  gridState,
  colDefs,
  filters,
  baseGridInitialState,
  sortKey,
  dirKey,
}: AugmentGridStateArgs): GridInitialStatePro {
  // Return the base state as a fallback...
  if (!gridState || !Array.isArray(colDefs)) {
    console.warn(
      '[augmentGridState] Invalid args, returning base state.',
      colDefs,
      gridState,
      filters,
    );
    return baseGridInitialState;
  }

  // Copies to avoid direct mutation
  const newGridState: Partial<GridInitialStatePro> = { ...gridState };
  const colDefsCopy: GenericGridColDefs = [...colDefs];

  // Handle sort/dir query params and set sort model if valid
  const sortField = filters?.[sortKey]?.toLowerCase();
  const sortOrder = filters?.[dirKey]?.toLowerCase();
  const isValidSort = sortField && (sortOrder === 'asc' || sortOrder === 'desc');

  // Apply sort state if valid
  if (isValidSort) {
    const sortModel: GridSortModel = [{ field: sortField, sort: sortOrder }];
    newGridState.sorting = { sortModel: sortModel };
  } else {
    // Clear grid sort model to empty - you may need to handle
    // updating state filters outside this function as needed
    newGridState.sorting = { sortModel: [] };
  }

  // Ensure all pinned columns are visible
  // and adjust order if pinnedColumns and columns exist
  if (newGridState.pinnedColumns && newGridState.columns) {
    const pinnedColumns = newGridState.pinnedColumns;
    const columns = newGridState.columns;

    // Ensure columns and columnVisibilityModel exist
    if (!columns.columnVisibilityModel) {
      columns.columnVisibilityModel = {};
    }

    // Ensure all pinned columns are visible
    if (pinnedColumns.left) {
      pinnedColumns.left.forEach((field) => {
        if (columns?.columnVisibilityModel && field in columns?.columnVisibilityModel) {
          columns.columnVisibilityModel[field] = true;
        }
      });
    }

    if (pinnedColumns.right) {
      pinnedColumns.right.forEach((field) => {
        if (columns?.columnVisibilityModel && field in columns?.columnVisibilityModel) {
          columns.columnVisibilityModel[field] = true;
        }
      });
    }

    // Ensure all columns exist in colDefs and handle undefined values in filtering
    const allColumns = colDefsCopy.map((col) => col.field);

    // Remove __check__ (GRID_CHECKBOX_SELECTION_FIELD) from all arrays to handle it separately
    const checkColumnInLeft = pinnedColumns.left?.includes(GRID_CHECKBOX_SELECTION_FIELD);
    const checkColumnInRight = pinnedColumns.right?.includes(
      GRID_CHECKBOX_SELECTION_FIELD,
    );

    // Filter out __check__ from pinned arrays if it exists there
    if (checkColumnInLeft && pinnedColumns.left) {
      pinnedColumns.left = pinnedColumns.left.filter(
        (field) => field !== GRID_CHECKBOX_SELECTION_FIELD,
      );
    }
    if (checkColumnInRight && pinnedColumns.right) {
      pinnedColumns.right = pinnedColumns.right.filter(
        (field) => field !== GRID_CHECKBOX_SELECTION_FIELD,
      );
    }

    const middleColumns = allColumns.filter((field) => {
      // Exclude checkbox selection field
      if (field === GRID_CHECKBOX_SELECTION_FIELD) {
        return false;
      }
      // Exclude pinned columns (left or right)
      if (pinnedColumns.left?.includes(field) || pinnedColumns.right?.includes(field)) {
        return false;
      }

      return true;
    });

    // Set the order of columns with the
    // __check__ col always first in middle cols
    columns.orderedFields = [
      ...(pinnedColumns.left || []),
      GRID_CHECKBOX_SELECTION_FIELD,
      ...middleColumns,
      ...(pinnedColumns.right || []),
    ];

    // Ensure __check__ is visible
    if (GRID_CHECKBOX_SELECTION_FIELD in columns?.columnVisibilityModel) {
      columns.columnVisibilityModel[GRID_CHECKBOX_SELECTION_FIELD] = true;
    }
  }

  // Force prefs panel closed if was open
  newGridState.preferencePanel = { open: false };

  // Return the new state for chaining
  return newGridState;
}

/**
 * Required to restore columns from state export
 * Format grid state coldefs to base coldef format
 * before further further augmentation.
 */

type BuildColDefsViaStateExportArgs = {
  gridState: GridInitialStatePro; // exported grid state
  colDefs: GenericGridColDefs; // initial colDefs memo
};

export function buildColDefsViaStateExport({
  gridState,
  colDefs,
}: BuildColDefsViaStateExportArgs): GenericGridColDefs {
  const orderedFields = gridState?.columns?.orderedFields ?? [];
  const dimensions = gridState?.columns?.dimensions ?? {};
  const colDefMap = new Map(colDefs.map((colDef) => [colDef?.field, colDef]));

  const formattedColDefs = orderedFields.reduce<GenericGridColDefs>((acc, field) => {
    const baseColDef = colDefMap.get(field);

    // Handle known auto-added or conditionally excluded fields silently
    const isAllowedMissingField = field === GRID_CHECKBOX_SELECTION_FIELD;

    if (!baseColDef) {
      if (!isAllowedMissingField) {
        // This is just a warning and functions fine
        // without it, but it's good to log if it happens.
        console.warn(`[buildColDefsViaStateExport] Missing coldef for field: ${field}`);
      }
      // Skip adding to the accumulator
      return acc;
    }

    const dimension = dimensions[field] || {};
    acc.push({
      ...baseColDef,
      minWidth: dimension?.minWidth ?? baseColDef?.minWidth,
      maxWidth: dimension?.maxWidth ?? baseColDef?.maxWidth,
      width: dimension?.width ?? baseColDef?.width,
      flex: dimension?.flex ?? baseColDef?.flex,
    });

    return acc;
  }, []);

  return formattedColDefs;
}

/**
 * Saves grid state to local storage
 */
type SaveGridStateLocalStorageArgs = {
  details: GridCallbackDetails<any>;
  resource: SupportedResources;
  view: string; // 'list' | 'summary'
  filters: SearchModalFilters;
  baseGridInitialState: GridInitialStatePro;
  colDefs: GenericGridColDefs; // via coldefs memo
  sortKey: string; // via sortDirConfig
  dirKey: string;
};

export function saveGridStateLocalStorage({
  details,
  resource,
  view,
  filters,
  colDefs,
  baseGridInitialState,
  sortKey,
  dirKey,
}: SaveGridStateLocalStorageArgs): void {
  // ColDefs sanity check
  if (!colDefs) {
    console.error('[saveGridStateLS] Missing colDefs:', colDefs);
    return;
  }

  // Get grid storage key for current view and resource
  const gridStorageKey = getGridStorageLSKey(resource, view);

  // Get current exported grid state via ref
  const gridExportedState = details?.api?.exportState();

  // Fallback to initial colDefs if neccessary
  // Run the true MUI DG State export through our augmentGridState helper
  const formattedColDefs = buildColDefsViaStateExport({
    gridState: gridExportedState,
    colDefs: colDefs,
  });

  const currentModifiedGridState = augmentGridState({
    gridState: gridExportedState,
    colDefs: formattedColDefs,
    filters: filters,
    baseGridInitialState: baseGridInitialState,
    sortKey: sortKey,
    dirKey: dirKey,
  });

  if (currentModifiedGridState) {
    try {
      const currentStorageState = localStorage.getItem(gridStorageKey);
      const parsedStorageState = currentStorageState
        ? JSON.parse(currentStorageState)
        : null;
      // Update the local storage if the state differs
      const hasDifference = !isDeepEqual(currentModifiedGridState, parsedStorageState);
      if (hasDifference) {
        const serializedState = JSON.stringify(currentModifiedGridState);
        localStorage.setItem(gridStorageKey, serializedState);
      }
    } catch (error) {
      // If there's an error, revert to the base state and log the error
      console.error('[saveGridStateLS] Error - using base:', error);
      // Try to get the base state / initialized again.
      const newGridState = augmentGridState({
        gridState: baseGridInitialState,
        colDefs: colDefs,
        filters: filters,
        baseGridInitialState: baseGridInitialState,
        sortKey: sortKey,
        dirKey: dirKey,
      });
      // Save the base state to storage, and restore to the grid...
      localStorage.setItem(gridStorageKey, JSON.stringify(newGridState));
      details.api?.restoreState(newGridState);
    }
  } else {
    // If the apiref failed to export the state, log an error:
    console.error('[saveGridStateLS] export failed:', currentModifiedGridState);
  }
}
